import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  ArrowLeft, Settings, Users, DollarSign, TrendingUp, TrendingDown,
  Shield, Save, Trash2, Plus, RefreshCw, Search, BarChart3, 
  Activity, Share2, Calendar, ArrowUpRight, ArrowDownRight, CreditCard, Edit2, Package,
  Download, Monitor, Apple, Terminal, AlertCircle, Mail
} from 'lucide-react';
import { Link, Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Chart colors
const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];

// Analytics Period Selector Component
const PeriodSelector = ({ value, onChange }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-32 bg-white/5 border-white/20 text-white">
      <SelectValue />
    </SelectTrigger>
    <SelectContent className="bg-slate-800 border-white/20">
      <SelectItem value="7d" className="text-white">Last 7 days</SelectItem>
      <SelectItem value="30d" className="text-white">Last 30 days</SelectItem>
      <SelectItem value="90d" className="text-white">Last 90 days</SelectItem>
      <SelectItem value="1y" className="text-white">Last year</SelectItem>
    </SelectContent>
  </Select>
);

// Stat Card with trend indicator
const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = "blue" }) => {
  const colorClasses = {
    blue: "text-blue-400",
    green: "text-green-400",
    yellow: "text-yellow-400",
    red: "text-red-400",
    purple: "text-purple-400"
  };
  
  return (
    <Card className="bg-white/10 border-white/20">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-blue-200 text-sm">{title}</p>
            <p className="text-3xl font-bold text-white">{value}</p>
            {subtitle && <p className="text-blue-300 text-xs mt-1">{subtitle}</p>}
            {trend !== undefined && (
              <div className={`flex items-center mt-2 ${trend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {trend >= 0 ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
                <span className="text-sm font-medium">{Math.abs(trendValue || trend)}%</span>
                <span className="text-xs ml-1 text-blue-300">vs last period</span>
              </div>
            )}
          </div>
          <Icon className={`h-8 w-8 ${colorClasses[color]}`} />
        </div>
      </CardContent>
    </Card>
  );
};

const AdminPage = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('analytics');
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('30d');

  // Stats
  const [stats, setStats] = useState(null);
  const [summary, setSummary] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [tradingData, setTradingData] = useState(null);
  const [referralData, setReferralData] = useState(null);

  // Platform settings
  const [settings, setSettings] = useState({
    signup_credits: 100,
    credit_value_usd: 0.01,
    min_topup_usd: 5,
    stripe_enabled: true,
    paypal_enabled: false,
    crypto_enabled: false,
    commission_enabled: true,
    min_client_version: '1.0.0',
    maintenance_mode: false
  });

  // SMTP/Email settings
  const [smtpSettings, setSmtpSettings] = useState({
    host: 'smtp.gmail.com',
    port: 587,
    user: '',
    password: '',
    from_email: '',
    from_name: 'OMNITrade',
    enabled: false
  });
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  // Password reset modal state
  const [passwordResetModal, setPasswordResetModal] = useState({
    show: false,
    email: '',
    tempPassword: ''
  });

  // Commission tiers
  const [commissionTiers, setCommissionTiers] = useState([]);
  const [newTier, setNewTier] = useState({
    name: '',
    min_trade_value: 0,
    max_trade_value: 100,
    fee_type: 'flat',
    fee_value: 5,
    order: 0
  });
  const [editingTier, setEditingTier] = useState(null);

  // Credit packages
  const [creditPackages, setCreditPackages] = useState([]);
  const [newPackage, setNewPackage] = useState({
    name: '',
    credits: 100,
    price_usd: 5,
    is_active: true,
    order: 0
  });
  const [editingPackage, setEditingPackage] = useState(null);

  // Users
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [totalUsers, setTotalUsers] = useState(0);

  // Desktop Releases
  const [desktopReleases, setDesktopReleases] = useState([]);
  const [newRelease, setNewRelease] = useState({
    version: '',
    release_notes: '',
    windows_url: '',
    windows_signature: '',
    macos_intel_url: '',
    macos_intel_signature: '',
    macos_arm_url: '',
    macos_arm_signature: '',
    linux_url: '',
    linux_signature: '',
    is_active: true
  });

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchStats();
      fetchSettings();
      fetchCommissionTiers();
      fetchCreditPackages();
      fetchUsers();
      fetchAnalytics();
      fetchDesktopReleases();
      fetchSmtpSettings();
    }
  }, [user]);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchAnalytics();
    }
  }, [period]);

  const fetchAnalytics = async () => {
    try {
      const [summaryRes, revenueRes, userRes, tradingRes, referralRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/summary`),
        axios.get(`${API}/admin/analytics/revenue?period=${period}`),
        axios.get(`${API}/admin/analytics/users?period=${period}`),
        axios.get(`${API}/admin/analytics/trading?period=${period}`),
        axios.get(`${API}/admin/analytics/referrals?period=${period}`)
      ]);
      
      setSummary(summaryRes.data);
      setRevenueData(revenueRes.data);
      setUserData(userRes.data);
      setTradingData(tradingRes.data);
      setReferralData(referralRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`);
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchCommissionTiers = async () => {
    try {
      const response = await axios.get(`${API}/admin/commission-tiers`);
      setCommissionTiers(response.data.tiers || []);
    } catch (error) {
      console.error('Failed to fetch commission tiers:', error);
    }
  };

  const fetchUsers = async (search = '') => {
    try {
      const response = await axios.get(`${API}/admin/users?limit=50&search=${encodeURIComponent(search)}`);
      setUsers(response.data.users || []);
      setTotalUsers(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchDesktopReleases = async () => {
    try {
      const response = await axios.get(`${API}/admin/desktop-releases`);
      setDesktopReleases(response.data.releases || []);
    } catch (error) {
      console.error('Failed to fetch desktop releases:', error);
    }
  };

  const fetchSmtpSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/smtp-settings`);
      setSmtpSettings(prev => ({
        ...prev,
        host: response.data.host || 'smtp.gmail.com',
        port: response.data.port || 587,
        user: response.data.user || '',
        from_email: response.data.from_email || '',
        from_name: response.data.from_name || 'OMNITrade',
        enabled: response.data.enabled || false,
        // Don't set password - keep blank for security
        password: ''
      }));
      setSmtpConfigured(response.data.configured || false);
    } catch (error) {
      console.error('Failed to fetch SMTP settings:', error);
    }
  };

  const handleSaveSmtpSettings = async () => {
    setLoading(true);
    try {
      const updateData = {
        host: smtpSettings.host,
        port: smtpSettings.port,
        user: smtpSettings.user,
        from_email: smtpSettings.from_email || smtpSettings.user,
        from_name: smtpSettings.from_name,
        enabled: smtpSettings.enabled
      };
      
      // Only include password if it was changed (not empty)
      if (smtpSettings.password) {
        updateData.password = smtpSettings.password;
      }
      
      await axios.put(`${API}/admin/smtp-settings`, updateData);
      toast.success('SMTP settings saved successfully!');
      fetchSmtpSettings();
    } catch (error) {
      console.error('Failed to save SMTP settings:', error);
      toast.error('Failed to save SMTP settings');
    } finally {
      setLoading(false);
    }
  };

  const handleTestSmtpConnection = async () => {
    setSmtpTesting(true);
    try {
      // Test with current form values
      const testData = {
        host: smtpSettings.host,
        port: smtpSettings.port,
        user: smtpSettings.user,
        password: smtpSettings.password
      };
      
      // If no password entered, test with stored settings
      const response = smtpSettings.password 
        ? await axios.post(`${API}/admin/smtp-settings/test`, testData)
        : await axios.post(`${API}/admin/smtp-settings/test`);
      
      if (response.data.success) {
        toast.success('SMTP connection successful!');
      } else {
        toast.error(`Connection failed: ${response.data.message}`);
      }
    } catch (error) {
      console.error('SMTP test failed:', error);
      toast.error(error.response?.data?.detail || 'SMTP test failed');
    } finally {
      setSmtpTesting(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address');
      return;
    }
    
    setSmtpTesting(true);
    try {
      await axios.post(`${API}/admin/smtp-settings/send-test-email?to_email=${encodeURIComponent(testEmail)}`);
      toast.success(`Test email sent to ${testEmail}!`);
    } catch (error) {
      console.error('Failed to send test email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSmtpTesting(false);
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/settings`, settings);
      toast.success('Platform settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTier = async () => {
    if (!newTier.name) {
      toast.error('Please enter a tier name');
      return;
    }
    setLoading(true);
    try {
      // Map frontend field names to backend expected names
      const tierPayload = {
        tier_name: newTier.name,
        min_trade_value: newTier.min_trade_value,
        max_trade_value: newTier.max_trade_value,
        fee_type: newTier.fee_type,
        fee_value: newTier.fee_value,
        order: newTier.order
      };
      await axios.post(`${API}/admin/commission-tiers`, tierPayload);
      toast.success('Commission tier created');
      setNewTier({
        name: '',
        min_trade_value: 0,
        max_trade_value: 100,
        fee_type: 'flat',
        fee_value: 5,
        order: commissionTiers.length
      });
      fetchCommissionTiers();
    } catch (error) {
      // Handle Pydantic validation errors (array of objects) and regular errors
      const detail = error.response?.data?.detail;
      let errorMessage = 'Failed to create tier';
      if (Array.isArray(detail)) {
        // Pydantic validation errors - extract messages
        errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (typeof detail === 'string') {
        errorMessage = detail;
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTier = async (tierId) => {
    if (!confirm('Delete this commission tier?')) return;
    try {
      await axios.delete(`${API}/admin/commission-tiers/${tierId}`);
      toast.success('Tier deleted');
      fetchCommissionTiers();
    } catch (error) {
      toast.error('Failed to delete tier');
    }
  };

  const handleEditTier = (tier) => {
    setEditingTier({
      id: tier.id,
      name: tier.tier_name || tier.name,
      min_trade_value: tier.min_trade_value,
      max_trade_value: tier.max_trade_value,
      fee_type: tier.fee_type,
      fee_value: tier.fee_value,
      order: tier.order
    });
  };

  const handleUpdateTier = async () => {
    if (!editingTier) return;
    
    try {
      setLoading(true);
      await axios.put(`${API}/admin/commission-tiers/${editingTier.id}`, {
        tier_name: editingTier.name,
        min_trade_value: editingTier.min_trade_value,
        max_trade_value: editingTier.max_trade_value,
        fee_type: editingTier.fee_type,
        fee_value: editingTier.fee_value,
        order: editingTier.order
      });
      toast.success('Tier updated successfully');
      setEditingTier(null);
      fetchCommissionTiers();
    } catch (error) {
      toast.error('Failed to update tier: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEditTier = () => {
    setEditingTier(null);
  };

  // Credit Package Functions
  const fetchCreditPackages = async () => {
    try {
      const response = await axios.get(`${API}/admin/credit-packages`);
      setCreditPackages(response.data || []);
    } catch (error) {
      console.error('Failed to fetch credit packages:', error);
    }
  };

  const handleCreatePackage = async () => {
    if (!newPackage.name) {
      toast.error('Please enter a package name');
      return;
    }
    if (newPackage.credits <= 0) {
      toast.error('Credits must be greater than 0');
      return;
    }
    if (newPackage.price_usd <= 0) {
      toast.error('Price must be greater than 0');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/admin/credit-packages`, newPackage);
      toast.success('Credit package created');
      setNewPackage({
        name: '',
        credits: 100,
        price_usd: 5,
        is_active: true,
        order: creditPackages.length
      });
      fetchCreditPackages();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to create package');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePackage = async (packageId, updates) => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/credit-packages/${packageId}`, updates);
      toast.success('Package updated');
      setEditingPackage(null);
      fetchCreditPackages();
    } catch (error) {
      toast.error('Failed to update package');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePackage = async (packageId) => {
    if (!confirm('Delete this credit package?')) return;
    try {
      await axios.delete(`${API}/admin/credit-packages/${packageId}`);
      toast.success('Package deleted');
      fetchCreditPackages();
    } catch (error) {
      toast.error('Failed to delete package');
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role?role=${newRole}`);
      toast.success('User role updated');
      fetchUsers(userSearch);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/status?is_active=${!currentStatus}`);
      toast.success(`User ${currentStatus ? 'disabled' : 'enabled'}`);
      fetchUsers(userSearch);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleSetUserStatus = async (userId, email, newStatus) => {
    const statusLabels = {
      'active': 'activate',
      'suspended': 'suspend',
      'banned': 'ban'
    };
    
    if (newStatus === 'banned' && !confirm(`⚠️ BAN user "${email}"?\n\nThis will:\n- Block all trading\n- Disable login\n- Prevent account access\n\nContinue?`)) {
      return;
    }
    
    if (newStatus === 'suspended' && !confirm(`Suspend user "${email}"?\n\nThis will temporarily block trading. Continue?`)) {
      return;
    }
    
    try {
      await axios.put(`${API}/admin/users/${userId}/account-status?status=${newStatus}`);
      toast.success(`User ${email} ${statusLabels[newStatus]}ed`);
      fetchUsers(userSearch);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!confirm(`⚠️ PERMANENTLY DELETE user "${email}"?\n\nThis will delete:\n- User account\n- Wallet and credits\n- All transactions\n- Trade history\n- Bot settings\n\nThis action CANNOT be undone!`)) return;
    
    // Double confirmation for safety
    if (!confirm(`Are you absolutely sure? Type the user's email to confirm deletion.`) || 
        prompt(`Type "${email}" to confirm deletion:`) !== email) {
      toast.info('Deletion cancelled');
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success(`User "${email}" deleted successfully`);
      fetchUsers(userSearch);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleAdjustCredits = async (userId, email) => {
    const amount = prompt(`Enter credit adjustment for ${email} (positive to add, negative to subtract):`);
    if (amount === null) return;
    
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) {
      toast.error('Invalid amount');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/users/${userId}/credits?amount=${numAmount}&description=Admin manual adjustment`);
      toast.success(`Adjusted ${numAmount} credits for ${email}`);
      fetchUsers(userSearch);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust credits');
    }
  };

  const handleResetUserPassword = async (userId, email) => {
    if (!confirm(`Reset password for "${email}"?\n\nA temporary password will be generated.${smtpConfigured && smtpSettings.enabled ? '\nThe user will receive it via email.' : '\nYou will need to share it manually (email not configured).'}`)) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/admin/users/${userId}/reset-password?send_email=${smtpConfigured && smtpSettings.enabled}`);
      
      if (response.data.email_sent) {
        toast.success(`Password reset! Email sent to ${email}`);
      } else {
        // Show temp password in a modal that allows copying
        const tempPassword = response.data.temp_password;
        setPasswordResetModal({
          show: true,
          email: email,
          tempPassword: tempPassword
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard!');
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Copied to clipboard!');
    }
  };

  // Desktop Release Handlers
  const handleCreateRelease = async () => {
    if (!newRelease.version) {
      toast.error('Please enter a version number');
      return;
    }
    
    // Validate version format
    if (!/^\d+\.\d+\.\d+$/.test(newRelease.version)) {
      toast.error('Version must be in format X.Y.Z (e.g., 1.2.3)');
      return;
    }
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('version', newRelease.version);
      params.append('release_notes', newRelease.release_notes);
      params.append('is_active', newRelease.is_active);
      
      if (newRelease.windows_url) {
        params.append('windows_url', newRelease.windows_url);
        if (newRelease.windows_signature) params.append('windows_signature', newRelease.windows_signature);
      }
      if (newRelease.macos_intel_url) {
        params.append('macos_intel_url', newRelease.macos_intel_url);
        if (newRelease.macos_intel_signature) params.append('macos_intel_signature', newRelease.macos_intel_signature);
      }
      if (newRelease.macos_arm_url) {
        params.append('macos_arm_url', newRelease.macos_arm_url);
        if (newRelease.macos_arm_signature) params.append('macos_arm_signature', newRelease.macos_arm_signature);
      }
      if (newRelease.linux_url) {
        params.append('linux_url', newRelease.linux_url);
        if (newRelease.linux_signature) params.append('linux_signature', newRelease.linux_signature);
      }
      
      await axios.post(`${API}/desktop/releases?${params.toString()}`);
      toast.success(`Release ${newRelease.version} created`);
      setNewRelease({
        version: '',
        release_notes: '',
        windows_url: '',
        windows_signature: '',
        macos_intel_url: '',
        macos_intel_signature: '',
        macos_arm_url: '',
        macos_arm_signature: '',
        linux_url: '',
        linux_signature: '',
        is_active: true
      });
      fetchDesktopReleases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create release');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleReleaseActive = async (releaseId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/desktop-releases/${releaseId}/toggle-active`);
      toast.success(currentStatus ? 'Release deactivated' : 'Release activated');
      fetchDesktopReleases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update release');
    }
  };

  const handleDeleteRelease = async (releaseId, version) => {
    if (!confirm(`Delete release ${version}? This cannot be undone.`)) return;
    
    try {
      await axios.delete(`${API}/admin/desktop-releases/${releaseId}`);
      toast.success(`Release ${version} deleted`);
      fetchDesktopReleases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete release');
    }
  };

  // Redirect non-admins
  if (!authLoading && (!isAuthenticated || user?.role !== 'admin')) {
    return <Navigate to="/" replace />;
  }

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  // Prepare chart data
  const revenueChartData = revenueData?.daily_data?.map(d => ({
    date: d.date.slice(5), // MM-DD format
    revenue: d.revenue,
    transactions: d.transactions
  })) || [];

  const userChartData = userData?.daily_signups?.map(d => ({
    date: d.date.slice(5),
    signups: d.new_users
  })) || [];

  const strategyPieData = tradingData ? Object.entries(tradingData.strategy_usage || {}).map(([name, value]) => ({
    name: name.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
    value
  })) : [];

  const exchangePieData = tradingData ? Object.entries(tradingData.exchange_usage || {}).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value
  })) : [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a1628] via-[#0f2847] to-[#0a1628]">
      {/* Password Reset Modal */}
      {passwordResetModal.show && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 max-w-md mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                <Shield className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Password Reset Successful</h3>
                <p className="text-sm text-slate-400">{passwordResetModal.email}</p>
              </div>
            </div>
            
            <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
              <p className="text-slate-300 text-sm mb-3">
                The user's temporary password is:
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-slate-700 text-green-400 px-4 py-3 rounded-lg font-mono text-lg select-all">
                  {passwordResetModal.tempPassword}
                </code>
                <Button
                  onClick={() => copyToClipboard(passwordResetModal.tempPassword)}
                  className="bg-blue-600 hover:bg-blue-700 px-3"
                  title="Copy to clipboard"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="14" height="14" x="8" y="8" rx="2" ry="2"/>
                    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>
                  </svg>
                </Button>
              </div>
            </div>
            
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-4">
              <p className="text-yellow-300 text-sm">
                Please share this password securely with the user. They should change it immediately after logging in.
              </p>
            </div>
            
            <Button
              onClick={() => setPasswordResetModal({ show: false, email: '', tempPassword: '' })}
              className="w-full bg-slate-700 hover:bg-slate-600"
            >
              Close
            </Button>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Dashboard
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Shield className="h-6 w-6" />
                Admin Panel
              </h1>
              <p className="text-gray-400 text-sm">Platform analytics & management</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <PeriodSelector value={period} onChange={setPeriod} />
            <Button
              variant="outline"
              size="icon"
              onClick={fetchAnalytics}
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Badge className="bg-red-500 text-white">Admin Access</Badge>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white/10 border-white/20">
            <TabsTrigger value="analytics" className="data-[state=active]:bg-white/20 text-white" data-testid="admin-analytics-tab">
              <BarChart3 className="mr-2 h-4 w-4" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="overview" className="data-[state=active]:bg-white/20 text-white" data-testid="admin-overview-tab">
              <TrendingUp className="mr-2 h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-white/20 text-white" data-testid="admin-settings-tab">
              <Settings className="mr-2 h-4 w-4" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="packages" className="data-[state=active]:bg-white/20 text-white" data-testid="admin-packages-tab">
              <CreditCard className="mr-2 h-4 w-4" />
              Packages
            </TabsTrigger>
            <TabsTrigger value="commissions" className="data-[state=active]:bg-white/20 text-white" data-testid="admin-commissions-tab">
              <DollarSign className="mr-2 h-4 w-4" />
              Commissions
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-white/20 text-white" data-testid="admin-users-tab">
              <Users className="mr-2 h-4 w-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="email" className="data-[state=active]:bg-white/20 text-white" data-testid="admin-email-tab">
              <Activity className="mr-2 h-4 w-4" />
              Email
            </TabsTrigger>
            <TabsTrigger value="releases" className="data-[state=active]:bg-white/20 text-white" data-testid="admin-releases-tab">
              <Download className="mr-2 h-4 w-4" />
              Releases
            </TabsTrigger>
          </TabsList>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            {/* Quick Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                title="Today's Revenue"
                value={`$${summary?.today?.revenue?.toFixed(2) || '0.00'}`}
                subtitle={`${summary?.today?.signups || 0} new signups`}
                icon={DollarSign}
                color="green"
              />
              <StatCard
                title="This Week"
                value={`$${summary?.week?.revenue?.toFixed(2) || '0.00'}`}
                subtitle={`${summary?.week?.signups || 0} new users`}
                icon={Calendar}
                color="blue"
              />
              <StatCard
                title="This Month"
                value={`$${summary?.month?.revenue?.toFixed(2) || '0.00'}`}
                subtitle={`${summary?.month?.signups || 0} new users`}
                icon={TrendingUp}
                trend={revenueData?.growth_pct}
                trendValue={revenueData?.growth_pct}
                color="purple"
              />
              <StatCard
                title="All Time"
                value={`$${summary?.all_time?.revenue?.toFixed(2) || '0.00'}`}
                subtitle={`${summary?.all_time?.users || 0} total users`}
                icon={Activity}
                color="yellow"
              />
            </div>

            {/* Revenue Chart */}
            <Card className="bg-white/10 border-white/20">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Revenue Overview
                </CardTitle>
                <CardDescription className="text-blue-200">
                  Daily revenue for the selected period
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={revenueChartData}>
                      <defs>
                        <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} />
                      <YAxis stroke="#94a3b8" fontSize={12} tickFormatter={(v) => `$${v}`} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                        labelStyle={{ color: '#94a3b8' }}
                        itemStyle={{ color: '#10b981' }}
                        formatter={(value) => [`$${value.toFixed(2)}`, 'Revenue']}
                      />
                      <Area type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} fill="url(#revenueGradient)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* User Growth & Strategy Usage */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* User Growth Chart */}
              <Card className="bg-white/10 border-white/20">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    User Growth
                  </CardTitle>
                  <CardDescription className="text-blue-200">
                    {userData?.active_users || 0} active / {userData?.total_users || 0} total ({userData?.active_rate || 0}% active)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={userChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} />
                        <YAxis stroke="#94a3b8" fontSize={12} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                          labelStyle={{ color: '#94a3b8' }}
                          itemStyle={{ color: '#3b82f6' }}
                        />
                        <Bar dataKey="signups" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Strategy Distribution */}
              <Card className="bg-white/10 border-white/20">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Activity className="h-5 w-5" />
                    Strategy Usage
                  </CardTitle>
                  <CardDescription className="text-blue-200">
                    Distribution of trading strategies
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={strategyPieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          labelLine={false}
                        >
                          {strategyPieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Exchange Usage & Referrals */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Exchange Distribution */}
              <Card className="bg-white/10 border-white/20">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Exchange Usage
                  </CardTitle>
                  <CardDescription className="text-blue-200">
                    Trading mode: {tradingData?.trading_mode?.paper || 0} paper, {tradingData?.trading_mode?.live || 0} live
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={exchangePieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          labelLine={false}
                        >
                          {exchangePieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Referral Stats */}
              <Card className="bg-white/10 border-white/20">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Share2 className="h-5 w-5" />
                    Referral Program
                  </CardTitle>
                  <CardDescription className="text-blue-200">
                    {referralData?.conversion_rate || 0}% conversion rate
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="p-4 bg-white/5 rounded-lg">
                      <p className="text-2xl font-bold text-white">{referralData?.total_referrals || 0}</p>
                      <p className="text-blue-200 text-sm">Total Referrals</p>
                    </div>
                    <div className="p-4 bg-white/5 rounded-lg">
                      <p className="text-2xl font-bold text-green-400">{referralData?.converted_referrals || 0}</p>
                      <p className="text-blue-200 text-sm">Converted</p>
                    </div>
                    <div className="p-4 bg-white/5 rounded-lg">
                      <p className="text-2xl font-bold text-yellow-400">{referralData?.total_bonus_credits_paid || 0}</p>
                      <p className="text-blue-200 text-sm">Bonuses Paid</p>
                    </div>
                  </div>
                  
                  {/* Top Referrers */}
                  {referralData?.top_referrers?.length > 0 && (
                    <div>
                      <p className="text-blue-100 font-medium mb-2">Top Referrers</p>
                      <div className="space-y-2">
                        {referralData.top_referrers.slice(0, 5).map((r, i) => (
                          <div key={i} className="flex justify-between items-center p-2 bg-white/5 rounded">
                            <span className="text-white text-sm">{r.email}</span>
                            <span className="text-green-400 text-sm">{r.referrals} ({r.converted} converted)</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Auth Methods */}
            <Card className="bg-white/10 border-white/20">
              <CardHeader>
                <CardTitle className="text-white">Authentication Methods</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-white/5 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-400">{userData?.users_by_auth_method?.email || 0}</p>
                    <p className="text-blue-200 text-sm">Email/Password</p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-lg text-center">
                    <p className="text-2xl font-bold text-red-400">{userData?.users_by_auth_method?.google || 0}</p>
                    <p className="text-blue-200 text-sm">Google OAuth</p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-400">{userData?.users_by_role?.admin || 0}</p>
                    <p className="text-blue-200 text-sm">Admins</p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-lg text-center">
                    <p className="text-2xl font-bold text-purple-400">{userData?.users_by_role?.user || 0}</p>
                    <p className="text-blue-200 text-sm">Regular Users</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Overview Tab - Original Stats */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard title="Total Users" value={stats?.total_users || 0} icon={Users} color="blue" />
              <StatCard title="Active Users" value={stats?.active_users || 0} icon={TrendingUp} color="green" />
              <StatCard title="Credits in Circulation" value={stats?.total_credits_in_circulation?.toFixed(0) || 0} icon={DollarSign} color="yellow" />
              <StatCard title="Total Revenue" value={`$${stats?.total_revenue_usd?.toFixed(2) || '0.00'}`} icon={DollarSign} color="green" />
            </div>

            <Card className="bg-white/10 border-white/20">
              <CardHeader>
                <CardTitle className="text-white">Quick Stats</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-white">
                  <div className="p-4 bg-white/5 rounded-lg">
                    <p className="text-blue-200 text-sm">Total Transactions</p>
                    <p className="text-xl font-semibold">{stats?.total_transactions || 0}</p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-lg">
                    <p className="text-blue-200 text-sm">Fees Collected</p>
                    <p className="text-xl font-semibold">{stats?.total_fees_collected_credits?.toFixed(0) || 0} credits</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <Card className="bg-white/10 border-white/20" data-testid="platform-settings-card">
              <CardHeader>
                <CardTitle className="text-white">Platform Settings</CardTitle>
                <CardDescription className="text-blue-200">Configure global platform parameters</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-blue-100">Signup Bonus Credits</Label>
                    <Input
                      type="number"
                      value={settings.signup_credits}
                      onChange={(e) => setSettings(prev => ({ ...prev, signup_credits: parseFloat(e.target.value) }))}
                      className="mt-2 bg-white/5 border-white/20 text-white"
                    />
                    <p className="text-xs text-blue-300 mt-1">Credits given to new users</p>
                  </div>
                  
                  <div>
                    <Label className="text-blue-100">Credit Value (USD)</Label>
                    <Input
                      type="number"
                      step="0.001"
                      value={settings.credit_value_usd}
                      onChange={(e) => setSettings(prev => ({ ...prev, credit_value_usd: parseFloat(e.target.value) }))}
                      className="mt-2 bg-white/5 border-white/20 text-white"
                    />
                    <p className="text-xs text-blue-300 mt-1">Value of 1 credit in USD</p>
                  </div>
                  
                  <div>
                    <Label className="text-blue-100">Minimum Top-up (USD)</Label>
                    <Input
                      type="number"
                      value={settings.min_topup_usd}
                      onChange={(e) => setSettings(prev => ({ ...prev, min_topup_usd: parseFloat(e.target.value) }))}
                      className="mt-2 bg-white/5 border-white/20 text-white"
                    />
                  </div>
                </div>

                <div className="border-t border-white/10 pt-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Payment Methods</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                      <div>
                        <Label className="text-blue-100">Stripe (Credit Cards)</Label>
                        <p className="text-xs text-blue-300">Accept credit/debit card payments</p>
                      </div>
                      <Switch
                        checked={settings.stripe_enabled}
                        onCheckedChange={(checked) => setSettings(prev => ({ ...prev, stripe_enabled: checked }))}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                      <div>
                        <Label className="text-blue-100">PayPal</Label>
                        <p className="text-xs text-blue-300">Accept PayPal payments</p>
                      </div>
                      <Switch
                        checked={settings.paypal_enabled}
                        onCheckedChange={(checked) => setSettings(prev => ({ ...prev, paypal_enabled: checked }))}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                      <div>
                        <Label className="text-blue-100">Crypto Payments</Label>
                        <p className="text-xs text-blue-300">Accept cryptocurrency payments</p>
                      </div>
                      <Switch
                        checked={settings.crypto_enabled}
                        onCheckedChange={(checked) => setSettings(prev => ({ ...prev, crypto_enabled: checked }))}
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t border-white/10 pt-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Version Control & Maintenance</h3>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-blue-100">Minimum Client Version</Label>
                      <Input
                        type="text"
                        placeholder="e.g., 1.0.0"
                        value={settings.min_client_version || '1.0.0'}
                        onChange={(e) => setSettings(prev => ({ ...prev, min_client_version: e.target.value }))}
                        className="mt-2 bg-white/5 border-white/20 text-white w-48"
                        data-testid="min-client-version-input"
                      />
                      <p className="text-xs text-blue-300 mt-1">
                        Desktop clients below this version will be blocked from trading.
                        Format: MAJOR.MINOR.PATCH (e.g., 1.2.3)
                      </p>
                    </div>
                    
                    <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                      <div>
                        <Label className="text-blue-100">Maintenance Mode</Label>
                        <p className="text-xs text-blue-300">Block all trading while performing maintenance</p>
                      </div>
                      <Switch
                        checked={settings.maintenance_mode || false}
                        onCheckedChange={(checked) => setSettings(prev => ({ ...prev, maintenance_mode: checked }))}
                        data-testid="maintenance-mode-toggle"
                      />
                    </div>
                    
                    {settings.maintenance_mode && (
                      <div className="p-3 bg-yellow-500/20 border border-yellow-500/30 rounded-lg">
                        <p className="text-yellow-300 text-sm flex items-center gap-2">
                          <Shield className="h-4 w-4" />
                          <strong>Warning:</strong> Maintenance mode is ON. All desktop client trading is blocked.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button onClick={handleSaveSettings} disabled={loading} className="bg-green-600 hover:bg-green-700">
                    <Save className="mr-2 h-4 w-4" />
                    {loading ? 'Saving...' : 'Save Settings'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Credit Packages Tab */}
          <TabsContent value="packages" className="space-y-6">
            <Card className="bg-white/10 border-white/20">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Credit Packages
                </CardTitle>
                <CardDescription className="text-blue-200">Configure credit packages for purchase</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Existing Packages */}
                <div className="space-y-3">
                  {creditPackages.map((pkg) => (
                    <div key={pkg.id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                      {editingPackage === pkg.id ? (
                        <div className="flex-1 grid grid-cols-4 gap-3 mr-4">
                          <Input
                            placeholder="Name"
                            defaultValue={pkg.name}
                            className="bg-white/10 border-white/20 text-white"
                            id={`edit-name-${pkg.id}`}
                          />
                          <Input
                            type="number"
                            placeholder="Credits"
                            defaultValue={pkg.credits}
                            className="bg-white/10 border-white/20 text-white"
                            id={`edit-credits-${pkg.id}`}
                          />
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="Price USD"
                            defaultValue={pkg.price_usd}
                            className="bg-white/10 border-white/20 text-white"
                            id={`edit-price-${pkg.id}`}
                          />
                          <div className="flex items-center gap-2">
                            <Switch
                              defaultChecked={pkg.is_active}
                              id={`edit-active-${pkg.id}`}
                            />
                            <Label className="text-blue-200 text-sm">Active</Label>
                          </div>
                        </div>
                      ) : (
                        <div className="flex-1 grid grid-cols-4 gap-4">
                          <div>
                            <p className="text-white font-medium">{pkg.name}</p>
                            <p className="text-blue-300 text-xs">ID: {pkg.id}</p>
                          </div>
                          <div>
                            <p className="text-white">{pkg.credits.toLocaleString()} credits</p>
                            <p className="text-blue-300 text-xs">${(pkg.price_usd / pkg.credits * 100).toFixed(2)} per 100</p>
                          </div>
                          <div>
                            <p className="text-white">${pkg.price_usd.toFixed(2)}</p>
                          </div>
                          <div>
                            <Badge className={pkg.is_active ? 'bg-green-500' : 'bg-gray-500'}>
                              {pkg.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                        </div>
                      )}
                      <div className="flex gap-2">
                        {editingPackage === pkg.id ? (
                          <>
                            <Button
                              size="sm"
                              onClick={() => {
                                const name = document.getElementById(`edit-name-${pkg.id}`).value;
                                const credits = parseInt(document.getElementById(`edit-credits-${pkg.id}`).value);
                                const price_usd = parseFloat(document.getElementById(`edit-price-${pkg.id}`).value);
                                const is_active = document.getElementById(`edit-active-${pkg.id}`).getAttribute('data-state') === 'checked';
                                handleUpdatePackage(pkg.id, { name, credits, price_usd, is_active });
                              }}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <Save className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingPackage(null)}
                              className="border-white/20 text-white hover:bg-white/10"
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingPackage(pkg.id)}
                              className="border-white/20 text-white hover:bg-white/10"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeletePackage(pkg.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                  {creditPackages.length === 0 && (
                    <p className="text-blue-300 text-center py-4">No credit packages configured. Add one below.</p>
                  )}
                </div>

                {/* Add New Package */}
                <div className="border-t border-white/10 pt-6">
                  <h4 className="text-white font-medium mb-4">Add New Package</h4>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div>
                      <Label className="text-blue-200">Package Name</Label>
                      <Input
                        placeholder="e.g., Gold Pack"
                        value={newPackage.name}
                        onChange={(e) => setNewPackage({ ...newPackage, name: e.target.value })}
                        className="bg-white/10 border-white/20 text-white mt-1"
                        data-testid="new-package-name"
                      />
                    </div>
                    <div>
                      <Label className="text-blue-200">Credits</Label>
                      <Input
                        type="number"
                        placeholder="100"
                        value={newPackage.credits}
                        onChange={(e) => setNewPackage({ ...newPackage, credits: parseInt(e.target.value) || 0 })}
                        className="bg-white/10 border-white/20 text-white mt-1"
                        data-testid="new-package-credits"
                      />
                    </div>
                    <div>
                      <Label className="text-blue-200">Price (USD)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="5.00"
                        value={newPackage.price_usd}
                        onChange={(e) => setNewPackage({ ...newPackage, price_usd: parseFloat(e.target.value) || 0 })}
                        className="bg-white/10 border-white/20 text-white mt-1"
                        data-testid="new-package-price"
                      />
                    </div>
                    <div>
                      <Label className="text-blue-200">Display Order</Label>
                      <Input
                        type="number"
                        value={newPackage.order}
                        onChange={(e) => setNewPackage({ ...newPackage, order: parseInt(e.target.value) || 0 })}
                        className="bg-white/10 border-white/20 text-white mt-1"
                      />
                    </div>
                    <div className="flex items-end">
                      <Button
                        onClick={handleCreatePackage}
                        disabled={loading}
                        className="w-full bg-green-600 hover:bg-green-700"
                        data-testid="create-package-btn"
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Add Package
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Commissions Tab */}
          <TabsContent value="commissions" className="space-y-6">
            <Card className="bg-white/10 border-white/20">
              <CardHeader>
                <CardTitle className="text-white">Commission Tiers</CardTitle>
                <CardDescription className="text-blue-200">Configure trade commission rates</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  {commissionTiers.length === 0 ? (
                    <p className="text-blue-200 text-sm">No commission tiers configured. Add one below.</p>
                  ) : (
                    commissionTiers.map((tier) => (
                      <div key={tier.id} className="p-4 bg-white/5 rounded-lg">
                        {editingTier?.id === tier.id ? (
                          // Edit mode
                          <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <Label className="text-blue-100">Tier Name</Label>
                                <Input
                                  value={editingTier.name}
                                  onChange={(e) => setEditingTier(prev => ({ ...prev, name: e.target.value }))}
                                  className="mt-2 bg-white/5 border-white/20 text-white"
                                />
                              </div>
                              <div>
                                <Label className="text-blue-100">Min Trade Value ($)</Label>
                                <Input
                                  type="number"
                                  value={editingTier.min_trade_value}
                                  onChange={(e) => setEditingTier(prev => ({ ...prev, min_trade_value: parseFloat(e.target.value) }))}
                                  className="mt-2 bg-white/5 border-white/20 text-white"
                                />
                              </div>
                              <div>
                                <Label className="text-blue-100">Max Trade Value ($)</Label>
                                <Input
                                  type="number"
                                  value={editingTier.max_trade_value}
                                  onChange={(e) => setEditingTier(prev => ({ ...prev, max_trade_value: parseFloat(e.target.value) }))}
                                  className="mt-2 bg-white/5 border-white/20 text-white"
                                />
                              </div>
                              <div>
                                <Label className="text-blue-100">Fee Type</Label>
                                <select
                                  value={editingTier.fee_type}
                                  onChange={(e) => setEditingTier(prev => ({ ...prev, fee_type: e.target.value }))}
                                  className="mt-2 w-full h-10 px-3 rounded-md bg-white/5 border border-white/20 text-white"
                                >
                                  <option value="flat">Flat (Credits)</option>
                                  <option value="percentage">Percentage</option>
                                </select>
                              </div>
                              <div>
                                <Label className="text-blue-100">Fee Value</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={editingTier.fee_value}
                                  onChange={(e) => setEditingTier(prev => ({ ...prev, fee_value: parseFloat(e.target.value) }))}
                                  className="mt-2 bg-white/5 border-white/20 text-white"
                                />
                              </div>
                              <div>
                                <Label className="text-blue-100">Order</Label>
                                <Input
                                  type="number"
                                  value={editingTier.order || 0}
                                  onChange={(e) => setEditingTier(prev => ({ ...prev, order: parseInt(e.target.value) }))}
                                  className="mt-2 bg-white/5 border-white/20 text-white"
                                />
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button onClick={handleUpdateTier} disabled={loading} className="bg-green-600 hover:bg-green-700">
                                Save Changes
                              </Button>
                              <Button onClick={handleCancelEditTier} variant="outline" className="border-white/20 text-white hover:bg-white/10">
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          // View mode
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="text-white font-medium">{tier.tier_name || tier.name}</p>
                              <p className="text-blue-200 text-sm">
                                ${tier.min_trade_value} - ${tier.max_trade_value || '∞'} | 
                                {tier.fee_type === 'flat' ? ` ${tier.fee_value} credits` : ` ${tier.fee_value}%`}
                                {tier.order !== undefined && <span className="text-blue-300 ml-2">(Order: {tier.order})</span>}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button variant="ghost" size="sm" onClick={() => handleEditTier(tier)} className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10">
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteTier(tier.id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>

                <div className="border-t border-white/10 pt-6">
                  <h4 className="text-white font-medium mb-4">Add New Tier</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-blue-100">Tier Name</Label>
                      <Input
                        value={newTier.name}
                        onChange={(e) => setNewTier(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g., Small Trades"
                        className="mt-2 bg-white/5 border-white/20 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-blue-100">Min Trade Value ($)</Label>
                      <Input
                        type="number"
                        value={newTier.min_trade_value}
                        onChange={(e) => setNewTier(prev => ({ ...prev, min_trade_value: parseFloat(e.target.value) }))}
                        className="mt-2 bg-white/5 border-white/20 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-blue-100">Max Trade Value ($)</Label>
                      <Input
                        type="number"
                        value={newTier.max_trade_value}
                        onChange={(e) => setNewTier(prev => ({ ...prev, max_trade_value: parseFloat(e.target.value) }))}
                        className="mt-2 bg-white/5 border-white/20 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-blue-100">Fee Type</Label>
                      <select
                        value={newTier.fee_type}
                        onChange={(e) => setNewTier(prev => ({ ...prev, fee_type: e.target.value }))}
                        className="mt-2 w-full h-10 px-3 rounded-md bg-white/5 border border-white/20 text-white"
                      >
                        <option value="flat">Flat (Credits)</option>
                        <option value="percentage">Percentage</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-blue-100">Fee Value</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={newTier.fee_value}
                        onChange={(e) => setNewTier(prev => ({ ...prev, fee_value: parseFloat(e.target.value) }))}
                        className="mt-2 bg-white/5 border-white/20 text-white"
                      />
                    </div>
                    <div className="flex items-end">
                      <Button onClick={handleCreateTier} disabled={loading} className="bg-blue-600 hover:bg-blue-700 w-full">
                        <Plus className="mr-2 h-4 w-4" />
                        Add Tier
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            <Card className="bg-white/10 border-white/20">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white">User Management</CardTitle>
                    <CardDescription className="text-blue-200">{totalUsers} total users</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Search users..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && fetchUsers(userSearch)}
                      className="w-64 bg-white/5 border-white/20 text-white"
                    />
                    <Button variant="outline" size="icon" onClick={() => fetchUsers(userSearch)} className="bg-white/10 border-white/20 text-white hover:bg-white/20">
                      <Search className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => { setUserSearch(''); fetchUsers(''); }} className="bg-white/10 border-white/20 text-white hover:bg-white/20">
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {users.map((u) => (
                    <div key={u.id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-white font-medium">{u.name || u.email}</p>
                          <Badge className={u.role === 'admin' ? 'bg-red-500' : 'bg-blue-500'}>{u.role}</Badge>
                          {u.status === 'suspended' && <Badge className="bg-yellow-500">Suspended</Badge>}
                          {u.status === 'banned' && <Badge className="bg-red-600">Banned</Badge>}
                          {!u.is_active && <Badge className="bg-gray-500">Disabled</Badge>}
                          {u.google_id && <Badge className="bg-orange-500">Google</Badge>}
                        </div>
                        <p className="text-blue-200 text-sm">{u.email}</p>
                        <p className="text-blue-300 text-xs">
                          Balance: {u.wallet_balance?.toFixed(0) || 0} credits | 
                          Joined: {new Date(u.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleAdjustCredits(u.id, u.email)} className="bg-white/10 border-white/20 text-white hover:bg-white/20">
                          <DollarSign className="h-4 w-4 mr-1" />
                          Credits
                        </Button>
                        
                        {/* User Status Dropdown */}
                        <Select 
                          value={u.status || 'active'} 
                          onValueChange={(value) => handleSetUserStatus(u.id, u.email, value)}
                          disabled={u.id === user?.id}
                        >
                          <SelectTrigger className="w-32 bg-white/10 border-white/20 text-white text-sm h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-white/20">
                            <SelectItem value="active" className="text-green-400">Active</SelectItem>
                            <SelectItem value="suspended" className="text-yellow-400">Suspend</SelectItem>
                            <SelectItem value="banned" className="text-red-400">Ban</SelectItem>
                          </SelectContent>
                        </Select>
                        
                        <Button variant="outline" size="sm" onClick={() => handleUpdateUserRole(u.id, u.role === 'admin' ? 'user' : 'admin')} className="bg-white/10 border-white/20 text-white hover:bg-white/20" disabled={u.id === user?.id}>
                          {u.role === 'admin' ? 'Demote' : 'Promote'}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleResetUserPassword(u.id, u.email)} 
                          disabled={u.id === user?.id}
                          className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                          title="Reset user password"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="destructive" 
                          size="sm" 
                          onClick={() => handleDeleteUser(u.id, u.email)} 
                          disabled={u.id === user?.id || u.role === 'admin'}
                          title={u.role === 'admin' ? 'Demote to user first' : 'Delete user'}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {users.length === 0 && (
                    <p className="text-blue-200 text-center py-8">No users found</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Email/SMTP Settings Tab */}
          <TabsContent value="email" className="space-y-6">
            {/* SMTP Configuration */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  SMTP Email Configuration
                </CardTitle>
                <p className="text-sm text-blue-200">
                  Configure SMTP settings to enable email verification and password reset functionality.
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Status Banner */}
                <div className={`p-4 rounded-lg border ${smtpConfigured ? 'bg-green-500/10 border-green-500/30' : 'bg-yellow-500/10 border-yellow-500/30'}`}>
                  <div className="flex items-center gap-2">
                    {smtpConfigured ? (
                      <>
                        <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
                        <span className="text-green-400 font-medium">SMTP Configured</span>
                        {smtpSettings.enabled ? (
                          <span className="text-green-300 text-sm ml-2">- Email features active</span>
                        ) : (
                          <span className="text-yellow-300 text-sm ml-2">- Currently disabled</span>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="w-3 h-3 rounded-full bg-yellow-500" />
                        <span className="text-yellow-400 font-medium">SMTP Not Configured</span>
                        <span className="text-yellow-300 text-sm ml-2">- Email features unavailable</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10">
                  <div>
                    <label className="text-white font-medium">Enable Email Service</label>
                    <p className="text-blue-200 text-sm">Turn on to enable email verification and password reset</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={smtpSettings.enabled} 
                      onChange={(e) => setSmtpSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                  </label>
                </div>

                {/* SMTP Settings Form */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-white text-sm font-medium">SMTP Host</label>
                    <Input
                      value={smtpSettings.host}
                      onChange={(e) => setSmtpSettings(prev => ({ ...prev, host: e.target.value }))}
                      placeholder="smtp.gmail.com"
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-white text-sm font-medium">SMTP Port</label>
                    <Input
                      type="number"
                      value={smtpSettings.port}
                      onChange={(e) => setSmtpSettings(prev => ({ ...prev, port: parseInt(e.target.value) || 587 }))}
                      placeholder="587"
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-white text-sm font-medium">SMTP User (Email)</label>
                    <Input
                      value={smtpSettings.user}
                      onChange={(e) => setSmtpSettings(prev => ({ ...prev, user: e.target.value }))}
                      placeholder="your-email@gmail.com"
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-white text-sm font-medium">
                      SMTP Password (App Password)
                      {smtpConfigured && <span className="text-green-400 ml-2 text-xs">• Set</span>}
                    </label>
                    <Input
                      type="password"
                      value={smtpSettings.password}
                      onChange={(e) => setSmtpSettings(prev => ({ ...prev, password: e.target.value }))}
                      placeholder={smtpConfigured ? "••••••••••••••••" : "Enter app password"}
                      className="bg-white/10 border-white/20 text-white"
                    />
                    <p className="text-blue-200 text-xs">For Gmail, use an App Password (not your regular password)</p>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-white text-sm font-medium">From Name</label>
                    <Input
                      value={smtpSettings.from_name}
                      onChange={(e) => setSmtpSettings(prev => ({ ...prev, from_name: e.target.value }))}
                      placeholder="OMNITrade"
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-white text-sm font-medium">From Email (optional)</label>
                    <Input
                      value={smtpSettings.from_email}
                      onChange={(e) => setSmtpSettings(prev => ({ ...prev, from_email: e.target.value }))}
                      placeholder="Uses SMTP user if empty"
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3 pt-4 border-t border-white/10">
                  <Button 
                    onClick={handleSaveSmtpSettings} 
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {loading ? 'Saving...' : 'Save Settings'}
                  </Button>
                  
                  <Button 
                    variant="outline"
                    onClick={handleTestSmtpConnection} 
                    disabled={smtpTesting || !smtpSettings.user}
                    className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                  >
                    {smtpTesting ? 'Testing...' : 'Test Connection'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Send Test Email */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Send Test Email
                </CardTitle>
                <p className="text-sm text-blue-200">
                  Send a test email to verify everything is working correctly.
                </p>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Input
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="recipient@example.com"
                    className="bg-white/10 border-white/20 text-white flex-1"
                  />
                  <Button 
                    onClick={handleSendTestEmail} 
                    disabled={smtpTesting || !smtpConfigured || !smtpSettings.enabled}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {smtpTesting ? 'Sending...' : 'Send Test Email'}
                  </Button>
                </div>
                {(!smtpConfigured || !smtpSettings.enabled) && (
                  <p className="text-yellow-400 text-sm mt-2">
                    {!smtpConfigured ? 'Configure and save SMTP settings first' : 'Enable email service to send test emails'}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Gmail Setup Guide */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-blue-400" />
                  Gmail SMTP Setup Guide
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-blue-200">
                <div className="space-y-2">
                  <h4 className="text-white font-medium">To use Gmail SMTP, you need an App Password:</h4>
                  <ol className="list-decimal list-inside space-y-1 text-sm">
                    <li>Go to your Google Account → Security</li>
                    <li>Enable 2-Step Verification if not already enabled</li>
                    <li>Go to App Passwords (search for it in Google Account)</li>
                    <li>Select "Mail" and "Other (Custom name)" → enter "OMNITrade"</li>
                    <li>Click Generate and copy the 16-character password</li>
                    <li>Use that password in the SMTP Password field above</li>
                  </ol>
                </div>
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <p className="text-yellow-300 text-sm">
                    <strong>Note:</strong> Regular Gmail passwords won't work. You must use an App Password for SMTP authentication.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Desktop Releases Tab */}
          <TabsContent value="releases" className="space-y-6">
            {/* Create New Release */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Plus className="h-5 w-5" />
                  Create New Release
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Basic Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-blue-100">Version *</Label>
                      <Input
                        placeholder="e.g., 1.2.0"
                        value={newRelease.version}
                        onChange={(e) => setNewRelease(prev => ({ ...prev, version: e.target.value }))}
                        className="mt-2 bg-white/5 border-white/20 text-white"
                        data-testid="release-version-input"
                      />
                      <p className="text-xs text-blue-300 mt-1">Format: MAJOR.MINOR.PATCH</p>
                    </div>
                    <div className="flex items-center gap-4 pt-6">
                      <Switch
                        checked={newRelease.is_active}
                        onCheckedChange={(checked) => setNewRelease(prev => ({ ...prev, is_active: checked }))}
                        data-testid="release-active-toggle"
                      />
                      <Label className="text-blue-100">Set as active release</Label>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-blue-100">Release Notes</Label>
                    <textarea
                      placeholder="What's new in this version..."
                      value={newRelease.release_notes}
                      onChange={(e) => setNewRelease(prev => ({ ...prev, release_notes: e.target.value }))}
                      className="mt-2 w-full bg-white/5 border border-white/20 text-white rounded-md p-3 min-h-[80px]"
                      data-testid="release-notes-input"
                    />
                  </div>

                  {/* Platform URLs */}
                  <div className="border-t border-white/10 pt-4">
                    <h4 className="text-white font-medium mb-4 flex items-center gap-2">
                      <Download className="h-4 w-4" />
                      Download URLs by Platform
                    </h4>
                    
                    {/* Windows */}
                    <div className="mb-4 p-4 bg-white/5 rounded-lg">
                      <div className="flex items-center gap-2 mb-3">
                        <Monitor className="h-4 w-4 text-blue-400" />
                        <Label className="text-blue-100 font-medium">Windows</Label>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-blue-200 text-xs">Download URL (.msi or .exe)</Label>
                          <Input
                            placeholder="https://..."
                            value={newRelease.windows_url}
                            onChange={(e) => setNewRelease(prev => ({ ...prev, windows_url: e.target.value }))}
                            className="mt-1 bg-white/5 border-white/20 text-white text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-blue-200 text-xs">Signature (optional)</Label>
                          <Input
                            placeholder="Base64 signature..."
                            value={newRelease.windows_signature}
                            onChange={(e) => setNewRelease(prev => ({ ...prev, windows_signature: e.target.value }))}
                            className="mt-1 bg-white/5 border-white/20 text-white text-sm"
                          />
                        </div>
                      </div>
                    </div>

                    {/* macOS Intel */}
                    <div className="mb-4 p-4 bg-white/5 rounded-lg">
                      <div className="flex items-center gap-2 mb-3">
                        <Apple className="h-4 w-4 text-gray-400" />
                        <Label className="text-blue-100 font-medium">macOS (Intel)</Label>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-blue-200 text-xs">Download URL (.dmg or .app.tar.gz)</Label>
                          <Input
                            placeholder="https://..."
                            value={newRelease.macos_intel_url}
                            onChange={(e) => setNewRelease(prev => ({ ...prev, macos_intel_url: e.target.value }))}
                            className="mt-1 bg-white/5 border-white/20 text-white text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-blue-200 text-xs">Signature (optional)</Label>
                          <Input
                            placeholder="Base64 signature..."
                            value={newRelease.macos_intel_signature}
                            onChange={(e) => setNewRelease(prev => ({ ...prev, macos_intel_signature: e.target.value }))}
                            className="mt-1 bg-white/5 border-white/20 text-white text-sm"
                          />
                        </div>
                      </div>
                    </div>

                    {/* macOS ARM */}
                    <div className="mb-4 p-4 bg-white/5 rounded-lg">
                      <div className="flex items-center gap-2 mb-3">
                        <Apple className="h-4 w-4 text-gray-400" />
                        <Label className="text-blue-100 font-medium">macOS (Apple Silicon)</Label>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-blue-200 text-xs">Download URL (.dmg or .app.tar.gz)</Label>
                          <Input
                            placeholder="https://..."
                            value={newRelease.macos_arm_url}
                            onChange={(e) => setNewRelease(prev => ({ ...prev, macos_arm_url: e.target.value }))}
                            className="mt-1 bg-white/5 border-white/20 text-white text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-blue-200 text-xs">Signature (optional)</Label>
                          <Input
                            placeholder="Base64 signature..."
                            value={newRelease.macos_arm_signature}
                            onChange={(e) => setNewRelease(prev => ({ ...prev, macos_arm_signature: e.target.value }))}
                            className="mt-1 bg-white/5 border-white/20 text-white text-sm"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Linux */}
                    <div className="mb-4 p-4 bg-white/5 rounded-lg">
                      <div className="flex items-center gap-2 mb-3">
                        <Terminal className="h-4 w-4 text-orange-400" />
                        <Label className="text-blue-100 font-medium">Linux</Label>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-blue-200 text-xs">Download URL (.AppImage or .deb)</Label>
                          <Input
                            placeholder="https://..."
                            value={newRelease.linux_url}
                            onChange={(e) => setNewRelease(prev => ({ ...prev, linux_url: e.target.value }))}
                            className="mt-1 bg-white/5 border-white/20 text-white text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-blue-200 text-xs">Signature (optional)</Label>
                          <Input
                            placeholder="Base64 signature..."
                            value={newRelease.linux_signature}
                            onChange={(e) => setNewRelease(prev => ({ ...prev, linux_signature: e.target.value }))}
                            className="mt-1 bg-white/5 border-white/20 text-white text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={handleCreateRelease}
                    disabled={loading || !newRelease.version}
                    className="bg-green-600 hover:bg-green-500"
                    data-testid="create-release-btn"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Create Release
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Existing Releases */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Package className="h-5 w-5" />
                    Desktop Releases
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchDesktopReleases}
                    className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {desktopReleases.map((release) => (
                    <div key={release._id || release.id} className="p-4 bg-white/5 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <span className="text-white font-semibold text-lg">v{release.version}</span>
                          {release.is_active && (
                            <Badge className="bg-green-500">Active</Badge>
                          )}
                          {!release.is_active && (
                            <Badge className="bg-gray-500">Inactive</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleReleaseActive(release._id || release.id, release.is_active)}
                            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                          >
                            {release.is_active ? 'Deactivate' : 'Activate'}
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteRelease(release._id || release.id, release.version)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      {release.release_notes && (
                        <p className="text-blue-200 text-sm mb-2">{release.release_notes}</p>
                      )}
                      <div className="text-xs text-blue-300">
                        <span>Created: {new Date(release.created_at).toLocaleString()}</span>
                        <span className="mx-2">|</span>
                        <span>Platforms: {Object.keys(release.download_urls || {}).join(', ') || 'None'}</span>
                      </div>
                    </div>
                  ))}
                  {desktopReleases.length === 0 && (
                    <p className="text-blue-200 text-center py-8">No releases found. Create your first release above.</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Signing Instructions */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Release Signing Guide
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-blue-200 text-sm space-y-4">
                  <p>For secure updates, sign your releases using the Tauri CLI:</p>
                  <div className="bg-black/30 p-4 rounded-lg font-mono text-xs">
                    <p className="text-green-400"># 1. Generate a signing key pair (one-time)</p>
                    <p className="text-white">npx @tauri-apps/cli signer generate -w ~/.tauri/omnitrade.key</p>
                    <p className="text-blue-300 mt-2"># Save the public key and add it to tauri.conf.json</p>
                    <p className="text-green-400 mt-4"># 2. Sign your release file</p>
                    <p className="text-white">npx @tauri-apps/cli signer sign -k ~/.tauri/omnitrade.key -p "password" target/release/bundle/msi/OmniTrade_1.2.0_x64_en-US.msi</p>
                    <p className="text-blue-300 mt-2"># This outputs a .sig file - paste its contents in the signature field above</p>
                  </div>
                  <p className="text-yellow-300">
                    <strong>Important:</strong> Without signatures, updates will still work but won't be cryptographically verified.
                    For production, always sign your releases.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;
